import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Link2, Pilcrow } from 'lucide-react';
import { cn } from '@/lib/utils';

const ToolBtn = ({ active, onClick, label, children, testId }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} aria-label={label} title={label} aria-pressed={!!active}
    data-testid={testId}
    className={cn('h-7 w-7 inline-flex items-center justify-center rounded transition-colors', active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted')}>
    {children}
  </button>
);

// TipTap rich-text editor (bold, italic, lists, links, paragraph). Emits sanitisable HTML.
export const RichTextEditor = forwardRef(({ initialHtml = '', onChange, placeholder, testId = 'rich-editor', onSubmit, disabled }, ref) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({
      heading: false, codeBlock: false, horizontalRule: false,
      link: { openOnClick: false, autolink: true, protocols: ['mailto'], HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' } },
    })],
    content: initialHtml,
    editable: !disabled,
    editorProps: {
      attributes: { class: 'comms-prose min-h-[84px] max-h-64 overflow-y-auto px-3 py-2 text-sm focus:outline-none', 'data-testid': testId, 'aria-label': placeholder || 'Message' },
      handleKeyDown: (_v, e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { onSubmit?.(); return true; } return false; },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.isEmpty ? '' : ed.getHTML()),
  });

  useImperativeHandle(ref, () => ({
    clear: () => editor?.commands.clearContent(true),
    setHtml: (html) => editor?.commands.setContent(html || '', { emitUpdate: false }),
    focus: () => editor?.commands.focus(),
    getText: () => editor?.getText({ blockSeparator: '\n' }) || '',
    insertText: (t) => editor?.chain().focus().insertContent(t).run(),
  }), [editor]);
  useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (https://…)', prev);
    if (url === null) return;
    if (!url) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: /^(https?:|mailto:)/i.test(url) ? url : `https://${url}` }).run();
  };

  if (!editor) return null;
  return (
    <div className="rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring/40">
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1" role="toolbar" aria-label="Formatting">
        <ToolBtn label="Paragraph" active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()} testId={`${testId}-paragraph`}><Pilcrow className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn label="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} testId={`${testId}-bold`}><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn label="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} testId={`${testId}-italic`}><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn label="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} testId={`${testId}-bullets`}><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn label="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} testId={`${testId}-numbers`}><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn label="Link" active={editor.isActive('link')} onClick={setLink} testId={`${testId}-link`}><Link2 className="w-3.5 h-3.5" /></ToolBtn>
      </div>
      <div className="relative">
        {editor.isEmpty && placeholder && <span className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">{placeholder}</span>}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
});
RichTextEditor.displayName = 'RichTextEditor';
